import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, HeadphonesIcon, Bot, MessageCircle, Mail, Sparkles } from 'lucide-react';
import { COMPANY_INFO } from '../config/company';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { LEGAL_CONTENT, fillPlaceholders } from '../config/legalContent';

// خريطة الأيقونات — من الملف المركزي (iconKey → أيقونة lucide)
const ICONS = {
  Bot: Bot,
  MessageCircle: MessageCircle,
  Mail: Mail,
};

// خرائط الألوان — كل خيار يأخذ لون خاص به
const COLOR_MAP = {
  blue:  { icon: 'text-[#1E9BD9]', bg: 'bg-[#1E9BD9]/10', border: 'border-[#1E9BD9]/30', btn: 'bg-[#1E9BD9] hover:bg-[#1786be]' },
  green: { icon: 'text-[#25D366]', bg: 'bg-[#25D366]/10', border: 'border-[#25D366]/30', btn: 'bg-[#25D366] hover:bg-[#20b858]' },
  sky:   { icon: 'text-[#0EA5E9]', bg: 'bg-[#0EA5E9]/10', border: 'border-[#0EA5E9]/30', btn: 'bg-[#0EA5E9] hover:bg-[#0284C7]' },
};

export default function Support() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const lang = isAr ? 'ar' : 'en';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  const S = LEGAL_CONTENT.support;
  // whatsapp يُنظّف من + و مسافات لأن wa.me لا يقبلها
  const placeholders = {
    email: COMPANY_INFO.email,
    phone: COMPANY_INFO.phoneDisplay,
    whatsapp: (COMPANY_INFO.social?.whatsapp || COMPANY_INFO.phone || '').replace(/\D/g, ''),
  };

  const handleAction = (opt) => {
    const url = fillPlaceholders(opt.action, placeholders);
    if (opt.actionType === 'route') {
      navigate(url);
    } else {
      // external / mailto
      window.open(url, opt.actionType === 'mailto' ? '_self' : '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'} data-testid="support-page">
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'} rounded-full blur-[100px]`} />
        <div className={`absolute bottom-20 end-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-[#25D366]/8' : 'bg-[#25D366]/5'} rounded-full blur-[100px]`} />
      </div>

      {/* Compact Header */}
      <section className="relative pt-10 pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#1E9BD9] hover:underline mb-6 text-sm" data-testid="support-back-home">
            <BackArrow className="h-4 w-4" />
            {isAr ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/10 border border-[#1E9BD9]/20' : 'bg-[#1E9BD9]/10'}`}>
              <HeadphonesIcon className="h-7 w-7 text-[#1E9BD9]" />
            </div>
            <div>
              <h1 className={`text-3xl sm:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="support-title">
                {S.title[lang]}
              </h1>
              <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                {S.subtitle[lang]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Options Grid */}
      <section className="relative pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className={`mb-8 text-center leading-relaxed max-w-2xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
            {S.intro[lang]}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {S.options.map((opt) => {
              const c = COLOR_MAP[opt.color] || COLOR_MAP.blue;
              const Icon = ICONS[opt.iconKey] || Mail;
              return (
                <button
                  key={opt.key}
                  onClick={() => handleAction(opt)}
                  data-testid={`support-option-${opt.key}`}
                  className={`group relative flex flex-col text-start rounded-2xl p-6 border-2 transition-all hover:scale-[1.02] hover:shadow-xl ${
                    isDarkMode
                      ? `bg-[#141414] ${opt.highlight ? c.border : 'border-white/10 hover:border-white/25'}`
                      : `bg-white ${opt.highlight ? c.border : 'border-[#E2E8F0] hover:border-[#94A3B8]'} shadow-sm`
                  }`}
                >
                  {/* Highlight badge */}
                  {opt.highlight && (
                    <span className={`absolute top-3 end-3 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full ${c.bg} ${c.icon}`}>
                      <Sparkles className="h-3 w-3" />
                      {opt[lang].badge}
                    </span>
                  )}

                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${c.bg}`}>
                    <Icon className={`h-7 w-7 ${c.icon}`} />
                  </div>

                  <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {opt[lang].title}
                  </h3>
                  <p className={`text-sm leading-relaxed flex-1 mb-4 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                    {opt[lang].description}
                  </p>

                  {!opt.highlight && (
                    <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full mb-3 ${isDarkMode ? 'bg-white/5 text-gray-400' : 'bg-slate-100 text-[#64748B]'}`}>
                      {opt[lang].badge}
                    </span>
                  )}

                  <span className={`inline-flex items-center justify-center gap-2 w-full text-white font-medium py-2.5 rounded-xl transition-colors ${c.btn}`}>
                    {opt[lang].cta}
                    <BackArrow className="h-4 w-4" />
                  </span>
                </button>
              );
            })}
          </div>

          {/* Footer note */}
          <div className={`mt-10 p-4 rounded-xl text-center ${isDarkMode ? 'bg-white/5 border border-white/10 text-gray-400' : 'bg-white border border-[#E2E8F0] text-[#64748B]'}`}>
            <p className="text-sm">{S.footer[lang]}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
